import React, { useCallback, useEffect, useMemo } from 'react'
import { postedMessageFailed } from '@components/Analytics/performanceAnalytics'
import { RichTextPreview as PlateRichTextPreview } from '@components/RichTextPlate/RichTextPreview'
import {
    ChannelMessageEvent,
    EventStatus,
    RiverTimelineEvent,
    TimelineEvent,
} from '@towns-protocol/sdk'
import { Box, Icon, Stack, TextButton } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { Channel, parseMiniappCommand } from 'use-towns-client'
import { isTownsAppUrlString } from 'utils/isTownsAppUrl'
import { getMessageBody } from 'utils/ztevent_util'

import { MessageStatusAnnotation } from '../MessageStatusAnnotation'

type Props = {
    event: TimelineEvent
    eventContent: ChannelMessageEvent
    attachedLinks: string[]
    channels: Channel[]
    onMentionClick?: (mentionName: string) => void
    onMentionHover?: (element?: HTMLElement, userId?: string) => void
    onRetrySend?: () => void
}

export const MessageBody = ({
    attachedLinks,
    eventContent,
    event,
    channels,
    onMentionClick,
    onMentionHover,
    onRetrySend,
}: Props) => {
    let body = getMessageBody(event.eventId, eventContent)
    const { isTouch } = useDevice()

    body = useHideAttachementLinks(body, attachedLinks)

    const miniappCommand = parseMiniappCommand(body)
    if (miniappCommand) {
        body = ''
    }

    // Track message send failures
    // failed events are only stored in memory (streamStateView), so this won't trigger again on refresh
    // the analytics module prevents duplicates of the same eventId from being tracked
    useEffect(() => {
        if (event.status === EventStatus.NOT_SENT) {
            postedMessageFailed({ eventId: event.eventId })
        }
    }, [event.eventId, event.status])

    const onRetryButtonClicked = useCallback(
        (event: React.MouseEvent) => {
            event.preventDefault()
            event.stopPropagation()
            onRetrySend?.()
        },
        [onRetrySend],
    )

    let statusAnnotation: MessageStatusAnnotation | undefined = undefined

    const shouldShowEditedBelowAttachments =
        eventContent.editsEventId !== undefined && shouldShowEditedLabelBelowAttachments(event)

    if (eventContent.editsEventId !== undefined && !shouldShowEditedBelowAttachments) {
        statusAnnotation = 'edited' as const
    } else if (event.status === EventStatus.NOT_SENT) {
        statusAnnotation = 'not-sent' as const
    }

    return (
        <>
            {body && (
                <PlateRichTextPreview
                    content={body}
                    statusAnnotation={statusAnnotation}
                    mentions={eventContent.mentions}
                    channels={channels}
                    onMentionClick={onMentionClick}
                    onMentionHover={isTouch ? undefined : onMentionHover}
                />
            )}

            {statusAnnotation === 'not-sent' && (
                <Stack horizontal>
                    <Stack
                        horizontal
                        alignItems="center"
                        insetTop="xs"
                        insetLeft="xxs"
                        tooltip="This message failed to send. Try again."
                    >
                        <Icon type="arrowCircle" paddingBottom="xxs" />
                        <TextButton color="default" onClick={onRetryButtonClicked}>
                            Retry
                        </TextButton>
                    </Stack>
                    <Box grow />
                </Stack>
            )}
        </>
    )
}

/**
 * hide links to attachments if they are not interwined with the message body
 */
const useHideAttachementLinks = (body: string, attachedLinks: string[]) => {
    return useMemo(() => {
        if (!attachedLinks?.length) {
            return body
        }
        const link = attachedLinks?.[0]

        if (isTownsAppUrlString(link)) {
            // body is a single markdown link to the attachment
            let pattern: string

            pattern = `[${link}](${link})`
            if (body.startsWith(pattern) || body.endsWith(pattern)) {
                return body.replace(pattern, '')
            }
            pattern = `<${link}>`
            if (body.startsWith(pattern) || body.endsWith(pattern)) {
                return body.replace(pattern, '')
            }
        }

        return body
    }, [attachedLinks, body])
}

function shouldShowEditedLabelBelowAttachments(event: TimelineEvent): boolean {
    if (event.content?.kind !== RiverTimelineEvent.ChannelMessage) {
        return false
    }

    const hasAttachments = Boolean(
        event.content.attachments && event.content.attachments.length > 0,
    )
    const hasBody = Boolean(event.content.body && event.content.body.trim().length > 0)

    return hasAttachments && !hasBody
}
